/**
 * Canonical legal-link metadata, surface-agnostic.
 *
 * The marketing surface renders the actual legal documents at /legal/<slug>
 * (see src/surfaces/marketing/v2/legal/docs.ts for the markdown registry).
 * Footers on every other surface (kickstart, OS) and email templates link
 * to the same documents via absolute URLs.
 *
 * Keep the slug + number + title here so footers don't need to import the
 * marketing surface's heavy markdown module just to render a list of links.
 *
 * The race-bib `number` is the IP-lock motif — preserve it everywhere a
 * legal link surfaces, not just on the marketing footer.
 */

export type LegalSlug = 'terms' | 'privacy' | 'refund' | 'ai-disclosure' | 'cookies';

export type LegalLink = {
  slug: LegalSlug;
  number: string;
  title: string;
  shortTitle: string;
};

export const LEGAL_LINK_LIST: readonly LegalLink[] = [
  { slug: 'terms', number: '01', title: 'Terms of Service', shortTitle: 'Terms' },
  { slug: 'privacy', number: '02', title: 'Privacy Policy', shortTitle: 'Privacy' },
  { slug: 'refund', number: '03', title: 'Refund Policy', shortTitle: 'Refund' },
  { slug: 'ai-disclosure', number: '04', title: 'AI Disclosure', shortTitle: 'AI Disclosure' },
  { slug: 'cookies', number: '05', title: 'Cookie Policy', shortTitle: 'Cookies' },
] as const;

/**
 * Absolute URL for a legal document. Use from kickstart + OS surfaces (which
 * live on subdomains kickstart.theaibrandlab.com / app.theaibrandlab.com) and
 * from email templates.
 *
 * The marketing surface renders the same documents at the relative
 * `/legal/<slug>` path on theaibrandlab.com itself.
 */
export const LEGAL_BASE_URL = 'https://theaibrandlab.com';

export function legalAbsoluteUrl(slug: LegalSlug): string {
  return `${LEGAL_BASE_URL}/legal/${slug}`;
}

/**
 * AI Brand Lab postal address line for email footers (CAN-SPAM compliance
 * for US recipients; AU equivalent under the Spam Act 2003 / ABN-bearer rule).
 * Single source of truth so updates land in every channel at once.
 */
export const AI_BRAND_LAB_ABN = '41 745 240 434';
export const AI_BRAND_LAB_POSTAL = 'AI Brand Lab · ABN 41 745 240 434 · Sydney, NSW, Australia';
